<template>
  <!--
      role og aria-modal manglede: for en skaermlaeser var det bare et
      afsnit midt i produktsiden, ikke en dialog der har taget over.
      aria-labelledby peger paa overskriften, saa dialogen har et navn.
  -->
  <div v-if="show" class="modal d-block" tabindex="-1"
       role="dialog" aria-modal="true" aria-labelledby="prisalarm-titel">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content border-0 shadow">
        <form @submit.prevent="saveAlert">
          <div class="modal-header bg-light border-0">
            <h5 class="modal-title" id="prisalarm-titel">
              <i class="bi bi-bell me-2 text-primary"></i>Opret prisalarm
            </h5>
            <button type="button" class="btn-close" @click="close" aria-label="Luk"></button>
          </div>
          <div class="modal-body">
            <div v-if="!isAuthenticated" class="alert alert-info border-0 d-flex align-items-center">
              <i class="bi bi-info-circle-fill me-3 fs-4 text-primary"></i>
              <div>
                <p class="mb-2">Du skal være logget ind for at oprette prisalarmer.</p>
                <div class="d-grid gap-2 d-md-flex">
                  <button type="button" class="btn btn-primary" @click="goToLogin">
                    <i class="bi bi-box-arrow-in-right me-1"></i> Log ind
                  </button>
                  <Link href="/register" class="btn btn-outline-primary">
                    <i class="bi bi-person-plus me-1"></i> Opret bruger
                  </Link>
                </div>
              </div>
            </div>
            
            <div v-else>
              <!-- Alert Type Toggle -->
              <div class="alert-type-selector mb-4">
                <div class="alert-type-tabs">
                  <button type="button" 
                    class="alert-tab" 
                    :class="{'active': alertType === 'target', 'has-alert': existingAlert?.target_price}" 
                    @click="alertType = 'target'">
                    <i class="bi bi-flag me-2"></i> Ønskepris
                  </button>
                  <button type="button" 
                    class="alert-tab" 
                    :class="{'active': alertType === 'drop', 'has-alert': existingAlert?.drop_amount || existingAlert?.notify_on_any_drop}" 
                    @click="alertType = 'drop'">
                    <i class="bi bi-graph-down-arrow me-2"></i> Prisfald
                  </button>
                  <button type="button" 
                    class="alert-tab" 
                    :class="{'active': alertType === 'stock', 'has-alert': existingAlert?.stock_alert_type}" 
                    @click="alertType = 'stock'">
                    <i class="bi bi-box-seam me-2"></i> Lager
                  </button>
                </div>
              </div>

              <!-- Target Price Alert Option -->
              <div v-if="alertType === 'target'" class="alert-option-panel">
                <div class="mb-3">
                  <label class="form-label fw-500">Giv mig besked når prisen når:</label>

                  <!--
                    Det svaere ved en prisalarm er at vaelge tallet: man ved
                    ikke om 2.400 er urealistisk eller alt for forsigtigt.
                    Forslaget er det laveste vi HAR maalt, ikke et gaet — og
                    begrundelsen staar ved siden af, saa man kan vurdere det.
                  -->
                  <button
                    v-if="forslag && !formData.target_price"
                    type="button"
                    class="forslag-knap"
                    @click="formData.target_price = forslag.pris"
                  >
                    <i class="bi bi-lightbulb"></i>
                    Foreslå {{ new Intl.NumberFormat('da-DK').format(forslag.pris) }} kr.
                    <small>{{ forslag.begrundelse }}</small>
                  </button>

                  <div class="input-group">
                    <input 
                      type="number" 
                      v-model="formData.target_price" 
                      class="form-control" 
                      placeholder="Indtast ønsket pris" 
                      min="1"
                    />
                    <span class="input-group-text">kr.</span>
                  </div>
                  <small class="text-muted mt-2 d-block">
                    Vi sender dig en notifikation, når produktet når den pris du ønsker
                  </small>
                </div>

                <div class="form-check custom-switch">
                  <input class="form-check-input" type="checkbox" id="recurringDropCheck" v-model="formData.recurring_drop_enabled" />
                  <label class="form-check-label" for="recurringDropCheck">
                    <span>Og herefter hver gang prisen falder med:</span>
                  </label>
                </div>
                <div v-if="formData.recurring_drop_enabled" class="input-group mt-2">
                  <input 
                    type="number" 
                    v-model="formData.recurring_drop_amount" 
                    class="form-control" 
                    placeholder="Indtast beløb" 
                    min="1"
                  />
                  <span class="input-group-text">kr.</span>
                </div>
              </div>

              <!-- Price Drop Alert Option -->
              <div v-else-if="alertType === 'drop'" class="alert-option-panel">
                <div class="mb-3">
                  <label class="form-label fw-500">Giv mig besked når prisen falder med:</label>
                  <div class="input-group">
                    <input 
                      type="number" 
                      v-model="formData.drop_amount" 
                      class="form-control" 
                      placeholder="Indtast beløb" 
                      min="1"
                    />
                    <span class="input-group-text">kr.</span>
                  </div>
                  <small class="text-muted mt-2 d-block">
                    Vi sender dig en notifikation, når produktet falder med dette beløb
                  </small>
                </div>

                <div class="form-check custom-switch">
                  <input class="form-check-input" type="checkbox" id="anyDropCheck" v-model="formData.notify_on_any_drop" />
                  <label class="form-check-label" for="anyDropCheck">
                    <span>Giv også besked ved ethvert prisfald</span>
                  </label>
                </div>
              </div>

              <!-- Stock Alert Option -->
              <div v-else-if="alertType === 'stock'" class="alert-option-panel">
                <div class="mb-3">
                  <label class="form-label fw-500">Giv mig besked når produktet er på lager:</label>
                  <div class="form-check">
                    <input 
                      class="form-check-input" 
                      type="radio" 
                      id="anyRetailer" 
                      value="any" 
                      v-model="formData.stock_alert_type" 
                      :disabled="hasInStockRetailer" 
                    />
                    <label class="form-check-label" for="anyRetailer">Hos enhver forhandler</label>
                  </div>
                  <div class="form-check">
                    <input 
                      class="form-check-input" 
                      type="radio" 
                      id="specificRetailer" 
                      value="specific" 
                      v-model="formData.stock_alert_type" 
                    />
                    <label class="form-check-label" for="specificRetailer">Hos en specifik forhandler</label>
                  </div>
                  <div v-if="formData.stock_alert_type === 'specific'" class="mt-2">
                    <select v-model="formData.specific_retailer" class="form-select">
                      <option 
                        v-for="retailer in outOfStockRetailers" 
                        :key="retailer.id" 
                        :value="retailer.id"
                      >
                        {{ retailer.name }}
                      </option>
                    </select>
                  </div>
                </div>
              </div>
              
              <!-- Common Options -->
              <div class="mt-4 pt-3 border-top">
                <div v-if="alertType === 'target' || alertType === 'drop'" class="form-check custom-switch">
                  <input class="form-check-input" type="checkbox" id="stockCheck" v-model="formData.only_in_stock" />
                  <label class="form-check-label" for="stockCheck">
                    <span>Kun når varen er på lager</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
          
          <div class="modal-footer border-0">
            <button type="button" class="btn btn-outline-secondary" @click="close">Annuller</button>
            <button v-if="isAuthenticated" type="submit" class="btn btn-primary" :disabled="!hasChanges">
              <i class="bi bi-bell me-1"></i>
              {{
                (alertType === 'target' && existingAlert?.target_price) ||
                (alertType === 'drop' && (existingAlert?.drop_amount || existingAlert?.notify_on_any_drop)) ||
                (alertType === 'stock' && existingAlert?.stock_alert_type)
                  ? 'Opdater prisalarm'
                  : 'Aktivér prisalarm'
              }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { router, Link } from '@inertiajs/vue3';

export default {
  name: 'PriceAlertModal',
  components: { Link },
  props: {
    show: { type: Boolean, default: false },
    productId: { type: Number, required: true },
    isAuthenticated: { type: Boolean, default: false },
    existingAlert: { type: Object, default: null }
  },
  data() {
    return {
      // Forslag til maalpris, regnet paa varens egen historik.
      forslag: null,
      alertType: 'target', // 'target', 'drop', or 'stock'
      formData: {
        product_id: this.productId,
        notify_on_any_drop: false,
        target_price: null,
        drop_amount: null,
        only_in_stock: false,
        recurring_drop_enabled: false,
        recurring_drop_amount: null,
        stock_alert_type: 'any',
        specific_retailer: ''
      },
      retailers: []
    };
  },
  watch: {
    existingAlert: {
      handler(newVal) {
        if (newVal) {
          this.formData = {
            product_id: this.productId,
            notify_on_any_drop: Boolean(newVal.notify_on_any_drop),
            target_price: newVal.target_price,
            drop_amount: newVal.drop_amount,
            only_in_stock: Boolean(newVal.only_in_stock),
            stock_alert_type: newVal.stock_alert_type || 'any',
            specific_retailer: newVal.specific_retailer || ''
          };
          
          // Set alert type based on existing data
          if (newVal.target_price) {
            this.alertType = 'target';
          } else if (newVal.drop_amount || newVal.notify_on_any_drop) {
            this.alertType = 'drop';
          } else if (newVal.stock_alert_type) {
            this.alertType = 'stock';
          }
        }
      },
      immediate: true
    },
    alertType(newType) {
      // When switching between types, clear irrelevant values
      if (newType === 'target') {
        this.formData.drop_amount = null;
        this.formData.notify_on_any_drop = false;
        this.formData.stock_alert_type = 'any';
        this.formData.specific_retailer = '';
      } else if (newType === 'drop') {
        this.formData.target_price = null;
        this.formData.stock_alert_type = 'any';
        this.formData.specific_retailer = '';
      } else if (newType === 'stock') {
        this.formData.target_price = null;
        this.formData.drop_amount = null;
        this.formData.notify_on_any_drop = false;
      }
    }
  },
  computed: {
    hasChanges() {
      if (!this.existingAlert) {
        return true;
      }
      return (
        this.formData.target_price !== this.existingAlert.target_price ||
        this.formData.drop_amount !== this.existingAlert.drop_amount ||
        this.formData.notify_on_any_drop !== this.existingAlert.notify_on_any_drop ||
        this.formData.only_in_stock !== this.existingAlert.only_in_stock ||
        this.formData.stock_alert_type !== this.existingAlert.stock_alert_type ||
        this.formData.specific_retailer !== this.existingAlert.specific_retailer
      );
    },
    hasInStockRetailer() {
      return this.retailers.some(retailer => retailer.in_stock);
    },
    outOfStockRetailers() {
      return this.retailers.filter(retailer => !retailer.in_stock);
    }
  },
  methods: {
    close() {
      this.$emit('close');
    },
    goToLogin() {
      const redirect = typeof window !== 'undefined' ? window.location.pathname : '/';
      router.visit('/login?redirect=' + encodeURIComponent(redirect));
    },
    async saveAlert() {
      if (!this.isAuthenticated) return;
      
      try {
        // Ruterne ligger i routes/api.php og har derfor /api foran. Uden det
        // svarede både oprettelse og opslag 404, knappen render ikke, og
        // prisalarmer kunne slet ikke bruges.
        await axios.post('/api/user/price-alert', this.formData);
        // Optionally show success message
        this.close();
      } catch (error) {
        console.error('Failed to save price alert:', error);
        // Handle error
      }
    },
    async fetchAlert() {
      try {
        const response = await axios.get(`/api/user/price-alert/${this.productId}`);
        // Forslaget foelger med i samme svar; se getPriceAlert.
        this.forslag = response.data?.forslag ?? null;
        if (response.data && Object.keys(response.data).length > 0) {
          // Set existing alert data if available
          this.formData = {
            product_id: this.productId,
            notify_on_any_drop: Boolean(response.data.notify_on_any_drop),
            target_price: response.data.target_price,
            drop_amount: response.data.drop_amount,
            only_in_stock: Boolean(response.data.only_in_stock),
            stock_alert_type: response.data.stock_alert_type || 'any',
            specific_retailer: response.data.specific_retailer || ''
          };
          
          // Set alert type based on fetched data
          if (response.data.target_price) {
            this.alertType = 'target';
          } else if (response.data.drop_amount || response.data.notify_on_any_drop) {
            this.alertType = 'drop';
          } else if (response.data.stock_alert_type) {
            this.alertType = 'stock';
          }
        }
      } catch (error) {
        console.error('Failed to fetch existing price alert:', error);
      }
    },
    async fetchRetailerPrices() {
      try {
        const response = await axios.get(`/product/${this.productId}/retailer-prices`);
        this.retailers = response.data.retailers.map(retailer => ({
          ...retailer,
          in_stock: Boolean(retailer.in_stock)
        }));
      } catch (error) {
        console.error('Failed to fetch retailer prices:', error);
      }
    },
  },
  mounted() {
    // Only fetch if we don't already have the data and user is authenticated
    if (!this.existingAlert && this.isAuthenticated) {
      this.fetchAlert();
    }
    this.fetchRetailerPrices();
  }
};
</script>

<style scoped>
.forslag-knap {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.1rem;
    width: 100%;
    margin-bottom: 0.6rem;
    padding: 0.5rem 0.7rem;
    border: 1px dashed var(--pc-accent);
    border-radius: var(--pc-radius);
    background: var(--pc-accent-light);
    color: var(--pc-accent);
    font-weight: 600;
    text-align: left;
    cursor: pointer;
}

.forslag-knap small {
    font-weight: 400;
    color: var(--pc-text-secondary);
}

.modal {
  background-color: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(3px);
}

.modal-dialog {
  max-width: 500px;
}

.modal-content {
  border-radius: 0.75rem;
  overflow: hidden;
}

.modal-header {
  padding: 1.2rem 1.5rem;
}

.modal-header .modal-title {
  color: #1E3A8A;
  font-weight: 600;
  display: flex;
  align-items: center;
}

.modal-body {
  padding: 1.5rem;
}

.modal-footer {
  padding: 1.2rem 1.5rem;
}

.btn-primary {
  background-color: #1E3A8A;
  border-color: #1E3A8A;
}

.btn-primary:hover {
  background-color: #152a60;
  border-color: #152a60;
}

.btn-outline-primary {
  color: #1E3A8A;
  border-color: #1E3A8A;
}

.btn-outline-primary:hover {
  background-color: #f0f4ff;
  color: #1E3A8A;
}

/* Custom form styles */
.form-label {
  font-weight: 500;
  color: #344767;
}

.form-control:focus {
  border-color: #1E3A8A;
  box-shadow: 0 0 0 0.25rem rgba(30, 58, 138, 0.1);
}

/* Alert Type Tabs */
.alert-type-selector {
  margin-bottom: 1.5rem;
}

.alert-type-tabs {
  display: flex;
  width: 100%;
  border-radius: 0.5rem;
  overflow: hidden;
  border: 1px solid #eaeaea;
}

.alert-tab {
  flex: 1;
  padding: 0.75rem 1rem;
  text-align: center;
  border: none;
  background-color: #f8f9fa;
  color: #6c757d;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.alert-tab:first-child {
  border-right: 1px solid #eaeaea;
}

.alert-tab.active {
  background-color: #1E3A8A;
  color: white;
}

.alert-tab.has-alert {
  background-color: #e0e7ff;
  color: #1E3A8A;
}

/* Custom switch styling */
.custom-switch {
  padding-left: 2.5rem;
  margin-bottom: 0.5rem;
}

.form-check-input {
  cursor: pointer;
}

.form-check-input:checked {
  background-color: #1E3A8A;
  border-color: #1E3A8A;
}

.form-check-label {
  cursor: pointer;
}

.alert-option-panel {
  background-color: #f8f9fa;
  border-radius: 0.5rem;
  padding: 1.25rem;
  border: 1px solid #eaeaea;
  margin-bottom: 1rem;
}

.input-group-text {
  background-color: #f0f4ff;
  color: #1E3A8A;
  border-color: #ced4da;
  font-weight: 500;
}

.fw-500 {
  font-weight: 500;
}

.text-primary {
  color: #1E3A8A !important;
}
</style>
